/**
 * Phone Number Normalization Utility
 * Handles various phone number formats and converts them to a consistent format
 */

/**
 * Normalize phone number for display (Canadian standard: +1 (XXX) XXX-XXXX)
 * @param {string} phoneNumber - Raw phone number from database
 * @returns {string} Formatted phone number in Canadian standard format
 */
export const normalizePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Remove all non-digit characters to get clean digits only
  let cleaned = String(phoneNumber).replace(/\D/g, '');

  // Check if number already has country code +1 (11+ digits)
  const alreadyHasCountryCode = cleaned.length >= 11 && cleaned.startsWith('1');

  if (!alreadyHasCountryCode) {
    // Add country code +1 only if not present
    cleaned = '1' + cleaned;
  } else {
    // Number already has +1, keep it and ensure we have exactly 10 digits after country code
    const remainingDigits = cleaned.slice(2); // Remove country code +1
    if (remainingDigits.length > 10) {
      cleaned = '1' + remainingDigits.slice(0, 10);
    } else {
      cleaned = '1' + remainingDigits;
    }
  }

  // ✅ Skip validation - always return formatted number even if invalid
  // This prevents crashes on bad phone numbers
  try {
    const formatted = `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    return formatted;
  } catch (e) {
    // Fallback for any edge cases
    console.warn(`Phone normalization error: "${phoneNumber}"`);
    return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
};

/**
 * Extract display name from customer data or phone number
 * @param {Object} customer - Customer object with firstname/lastname (optional)
 * @param {string} phoneNumber - Phone number from conversation
 * @returns {string} Display name for the contact
 */
export const getContactDisplayName = (customer, phoneNumber) => {
  // First priority: Use customer name if available
  if (customer && (customer.firstname || customer.lastname)) {
    const fullName = `${customer.firstname} ${customer.lastname}`.trim();
    return fullName;
  }

  // Second priority: Try to extract initials from phone number prefix (rare case)
  if (!phoneNumber) return 'Unknown Contact';

  const match = String(phoneNumber).match(/^([A-Za-z]+)/);
  if (match) {
    return match[0];
  }

  // Third priority: Fall back to phone number itself when no customer name exists
  // This ensures we always show some identifying information
  if (phoneNumber) {
    const cleanedDigits = String(phoneNumber).replace(/\D/g, '');
    return cleanedDigits || 'Unknown Contact';
  }

  return 'Unknown Contact';
};

/**
 * Get avatar initials from customer data or contact name
 * @param {Object} customer - Customer object
 * @param {string} phoneNumber - Phone number
 * @returns {string} Initials or generic icon
 */
export const getContactInitials = (customer, phoneNumber) => {
  // Priority 1: Use customer firstname
  if (customer && customer.firstname) {
    return customer.firstname[0].toUpperCase();
  }

  // Priority 2: Try to extract initials from phone number prefix
  if (!phoneNumber) return '📞';

  const match = String(phoneNumber).match(/^([A-Za-z]{2,})/);
  if (match) {
    // Extract first letter or first 2 letters if available
    let initials = match[0].charAt(0).toUpperCase();
    if (match[0].length >= 2) {
      initials += match[0].charAt(1).toUpperCase();
    }
    return initials;
  }

  // Priority 3: Generic icon for phone-only contacts with no alphabetic prefix
  return '📞';
};
